import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { verifySalonMagicLink } from "../lib/api";

export default function SalonVerify() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState({ phase: "verifying", message: "" });
  // StrictMode fires effects twice in dev. Magic-link tokens are single-use,
  // so the second call returns 410 and overrides the success state. Guard it.
  const verifiedOnce = useRef(false);

  useEffect(() => {
    if (verifiedOnce.current) return;
    verifiedOnce.current = true;
    const token = params.get("token");
    if (!token) {
      setState({ phase: "error", message: "Missing sign-in token." });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await verifySalonMagicLink(token);
        if (cancelled) return;
        setState({ phase: "ok", message: `Welcome back to ${res?.salon?.name || "FYLLR"}.` });
        setTimeout(() => navigate("/salon/admin"), 800);
      } catch (e) {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || "This sign-in link is invalid or expired.";
        setState({ phase: "error", message: typeof msg === "string" ? msg : "Sign-in failed." });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pt-14 md:pt-16 min-h-screen flex items-center bg-bone" data-testid="salon-verify-page">
      <div className="max-w-md w-full mx-auto px-5 md:px-8 py-12 text-center">
        {state.phase === "verifying" && (
          <>
            <Loader2 size={22} strokeWidth={1.5} className="text-mocha mx-auto animate-spin" />
            <h1 className="mt-4 font-serif text-3xl md:text-4xl text-ink leading-tight">Signing you in…</h1>
            <p className="mt-3 text-sm text-mocha">One moment.</p>
          </>
        )}
        {state.phase === "ok" && (
          <>
            <CheckCircle2 size={22} strokeWidth={1.5} className="text-mocha mx-auto" />
            <h1 className="mt-4 font-serif text-3xl md:text-4xl text-ink leading-tight">You're in.</h1>
            <p className="mt-3 text-sm text-mocha">{state.message}</p>
          </>
        )}
        {state.phase === "error" && (
          <>
            <XCircle size={22} strokeWidth={1.5} className="text-mocha mx-auto" />
            <h1 className="mt-4 font-serif text-3xl md:text-4xl text-ink leading-tight">Sign-in failed.</h1>
            <p className="mt-3 text-sm text-mocha">{state.message}</p>
            <Link
              to="/salon/login"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-espresso text-bone px-6 py-2.5 text-sm hover:bg-ink transition-colors"
              data-testid="salon-verify-retry"
            >
              Send a new link
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
